/**
 * Trunnion AI - Button Component
 * Variants: primary, secondary, danger, ghost
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-trunnion-gold-500 text-trunnion-slate-950 hover:bg-trunnion-gold-400 active:bg-trunnion-gold-600 shadow-sm',
  secondary:
    'bg-trunnion-slate-700 text-trunnion-slate-100 hover:bg-trunnion-slate-600 active:bg-trunnion-slate-800 border border-trunnion-slate-600',
  danger:
    'bg-red-600 text-white hover:bg-red-500 active:bg-red-700 shadow-sm',
  ghost:
    'bg-transparent text-trunnion-slate-300 hover:bg-trunnion-slate-800 hover:text-white',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1.5 text-xs gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'px-6 py-2.5 text-base gap-2.5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      children,
      disabled,
      className = '',
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={`inline-flex items-center justify-center rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trunnion-gold-500 focus-visible:ring-offset-2 focus-visible:ring-offset-trunnion-slate-950 disabled:opacity-50 disabled:cursor-not-allowed ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
        {...props}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : icon ? (
          <span className="shrink-0">{icon}</span>
        ) : null}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
